// Recent subscriber lookups, persisted in localStorage — the React port
// of the PHP search page's `noc_recent_searches` list (most-recent-first,
// de-duplicated case-insensitively, capped). Shown in the search dropdown
// when the box is empty/focused so a rep can re-open the last few people
// they checked with one tap.

import { useCallback, useEffect, useState } from "react";

const KEY = "noc.recentSearches";
const MAX = 8;

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string").slice(0, MAX) : [];
  } catch {
    return [];
  }
}

function write(list: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    /* ignore */
  }
}

export function useRecentSearches() {
  const [recent, setRecent] = useState<string[]>(read);

  // Keep multiple tabs roughly in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setRecent(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const push = useCallback((q: string) => {
    const v = q.trim();
    if (!v) return;
    setRecent((prev) => {
      const next = [v, ...prev.filter((x) => x.toLowerCase() !== v.toLowerCase())].slice(0, MAX);
      write(next);
      return next;
    });
  }, []);

  const remove = useCallback((q: string) => {
    setRecent((prev) => {
      const next = prev.filter((x) => x !== q);
      write(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    write([]);
    setRecent([]);
  }, []);

  return { recent, push, remove, clear };
}
